import { useCallback } from "react";

import Box from "ds/components/Box";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Icon from "ds/components/Icon";
import { Copy, Download, Terminal, Trash } from "components/icons/generated";
import { Spinner } from "components/Spinner";
import useLazyCopyToClipboard from "hooks/useLazyCopyToClipboard";

import AssistantPanelLayoutHeaderModeDropdown from "../../Layout/Header/ModeDropdown";
import { getCodingAgentHandoffPrompt } from "../helpers";
import useAssistantPanelChatCopyTranscript from "../hooks/useCopyTranscript";
import useAssistantPanelChatDownloadTranscript from "../hooks/useDownloadTranscript";

const NO_CHAT_TOOLTIP = "Start a conversation to copy";

type AssistantPanelChatConversationActionsProps = {
  chatId: string | undefined;
  onDeleteChat: (chatId: string) => void;
};

const AssistantPanelChatConversationActions = ({
  chatId,
  onDeleteChat,
}: AssistantPanelChatConversationActionsProps) => {
  const copyToClipboard = useLazyCopyToClipboard();
  const { copyTranscript, loading: copyLoading } = useAssistantPanelChatCopyTranscript(chatId);
  const { downloadTranscript, loading: downloadLoading } =
    useAssistantPanelChatDownloadTranscript(chatId);

  const handleCopyCodingAgentHandoffPrompt = useCallback(() => {
    if (chatId) {
      copyToClipboard(getCodingAgentHandoffPrompt(chatId));
    }
  }, [chatId, copyToClipboard]);

  const handleDeleteChat = useCallback(() => {
    if (chatId) {
      onDeleteChat(chatId);
    }
  }, [chatId, onDeleteChat]);

  return (
    <AssistantPanelLayoutHeaderModeDropdown
      dangerActions={
        <DropdownMenuItem
          danger
          isDisabled={!chatId}
          tooltip="Open a chat to delete it"
          isTooltipActive={!chatId}
          tooltipWidthMode="maxWidthSm"
          onAction={handleDeleteChat}
        >
          <Box gap="medium" align="center">
            <Icon src={Trash} />
            Delete chat
          </Box>
        </DropdownMenuItem>
      }
    >
      <DropdownMenuItem
        isDisabled={!chatId || copyLoading}
        tooltip={NO_CHAT_TOOLTIP}
        isTooltipActive={!chatId}
        tooltipWidthMode="maxWidthSm"
        onAction={copyTranscript}
      >
        <Box gap="medium" align="center">
          <Icon src={copyLoading ? Spinner : Copy} />
          Copy as Markdown
        </Box>
      </DropdownMenuItem>
      <DropdownMenuItem
        isDisabled={!chatId || downloadLoading}
        tooltip="Start a conversation to download"
        isTooltipActive={!chatId}
        tooltipWidthMode="maxWidthSm"
        onAction={downloadTranscript}
      >
        <Box gap="medium" align="center">
          <Icon src={downloadLoading ? Spinner : Download} />
          Download as Markdown
        </Box>
      </DropdownMenuItem>
      <DropdownMenuItem
        isDisabled={!chatId}
        tooltip={NO_CHAT_TOOLTIP}
        isTooltipActive={!chatId}
        tooltipWidthMode="maxWidthSm"
        onAction={handleCopyCodingAgentHandoffPrompt}
      >
        <Box gap="medium" align="center">
          <Icon src={Terminal} />
          Copy as prompt
        </Box>
      </DropdownMenuItem>
    </AssistantPanelLayoutHeaderModeDropdown>
  );
};

export default AssistantPanelChatConversationActions;
